import { useCallback } from 'react'
import { gql, useApolloClient } from '@apollo/client'
import produce from 'immer'

import {
  GetNotificationsCountDocument,
  GetNotificationsCountQuery,
  GetNotificationsCountQueryVariables,
  GetUserNotificationsCountDocument,
  GetUserNotificationsCountQuery,
  NotificationInfoFragment,
  useUpdateNotificationMutation,
} from '@sketch/gql-types'

import { useInboxSearchContext } from '../../views/Inbox/InboxSearchContext'
import { buildSearchString } from '../../views/Inbox/searchUtils'

// `NotificationState` lives in gql-types' expansiveTypes and isn't re-exported
// from the package barrel, so derive the enum from the fragment field instead.
type NotificationState = NotificationInfoFragment['state']

// Concrete notification typenames (e.g. `CommentNotification`) — there's no
// `Notification` type; they all implement the `UserNotification` interface and
// are normalized in the cache under their concrete name.
type NotificationTypename = NotificationInfoFragment['__typename']

// The current fields are read from the cache before the mutation: `isPinned` is
// echoed back unchanged in the optimistic response (the mutation selects it),
// and the previous `state` tells us which way to nudge the unread count.
const NOTIFICATION_OPTIMISTIC_FIELDS = gql`
  fragment NotificationOptimisticFields on UserNotification {
    isPinned
    state
  }
`

/**
 * Returns a function that updates a notification's `state` via
 * `updateNotification`.
 *
 * The mutation now selects `state`, so Apollo normalizes the new value straight
 * onto the cached notification (clearing/restoring the unread dot in the list).
 * An `optimisticResponse` makes that happen before the request resolves — and
 * roll back if it fails. The unread-count badge reads from a separate aggregate
 * query, so it's nudged by hand for instant feedback and refetched afterwards to
 * reconcile with the server.
 *
 * The inbox empty state shows a second aggregate — the unread count for the
 * active filter (`getNotificationsCount`). It's nudged the same way, scoped to
 * the active filter's cache field: a notification acted on from the inbox is
 * always part of the filtered list it's shown in, so the same delta applies.
 * Any drift in other cached filters is reconciled by that query's
 * `cache-and-network` policy when it's next viewed.
 */
export const useSetNotificationState = () => {
  const client = useApolloClient()
  const { search, options } = useInboxSearchContext()
  const [updateNotification] = useUpdateNotificationMutation({
    onError: 'show-toast',
    redirectErrors: true,
    refetchQueries: [{ query: GetUserNotificationsCountDocument }],
  })

  return useCallback(
    (
      identifier: string,
      typename: NotificationTypename,
      state: NotificationState
    ) => {
      const id = client.cache.identify({ __typename: typename, identifier })

      const current = id
        ? client.cache.readFragment<{
            isPinned: boolean
            state: NotificationState
          }>({ id, fragment: NOTIFICATION_OPTIMISTIC_FIELDS })
        : null

      const isPinned = current?.isPinned ?? false

      // +1 when the notification becomes unread, -1 when it leaves unread, 0 if
      // the unread-ness is unchanged.
      const unreadDelta =
        Number(state === 'UNREAD') - Number(current?.state === 'UNREAD')

      // The per-filter empty-state count is keyed by the active filter, so build
      // the same search string the count query uses to target its cache field.
      // Empty means no filter is applied — in which case the count query is
      // skipped and its `{ states: [UNREAD] }` args would collide with the
      // global unread-count field, double-counting it. So only nudge it when a
      // filter actually narrows the list.
      const filteredSearch = buildSearchString({ search, options })
      const filteredCountVariables: GetNotificationsCountQueryVariables = {
        filter: { search: filteredSearch, states: ['UNREAD'] },
      }

      updateNotification({
        variables: { identifier, input: { state } },
        optimisticResponse: {
          __typename: 'RootMutationType',
          updateNotification: {
            __typename: 'NotificationUpdatedPayload',
            successful: true,
            notification: { __typename: typename, identifier, isPinned, state },
            errors: [],
          },
        },
        // `state` on the notification is normalized from the response above; only
        // the separate unread-count aggregates need a manual nudge.
        update: cache => {
          if (unreadDelta === 0) return

          // Global unread badge.
          cache.updateQuery<GetUserNotificationsCountQuery>(
            { query: GetUserNotificationsCountDocument },
            data =>
              produce(data, draft => {
                const meta = draft?.me.unreadNotificationsCount?.meta
                if (meta) {
                  meta.totalCount = Math.max(0, meta.totalCount + unreadDelta)
                }
              })
          )

          // Inbox empty-state count for the active filter. Only nudge a field
          // that's already cached — writing into an unfetched filter would
          // fabricate a count out of nothing.
          if (!filteredSearch) return
          cache.updateQuery<
            GetNotificationsCountQuery,
            GetNotificationsCountQueryVariables
          >(
            {
              query: GetNotificationsCountDocument,
              variables: filteredCountVariables,
            },
            data => {
              if (!data?.me?.notifications?.meta) return undefined
              return produce(data, draft => {
                const meta = draft?.me?.notifications?.meta
                if (meta) {
                  meta.totalCount = Math.max(0, meta.totalCount + unreadDelta)
                }
              })
            }
          )
        },
      })
    },
    [client, updateNotification, search, options]
  )
}
